/*
 * UserChoice Component
 * 
 * Purpose: Renders a chat interface component that displays user choice options
 * with muted text styling and comprehensive dark mode support. When a button is
 * clicked, adds the button text to the chat input and submits the message directly.
 * 
 * Logic:
 * - Displays multiple choice buttons arranged in a grid layout
 * - Each option button includes contextual icons based on option text content
 * - Uses muted color palette for text and backgrounds with subtle hover effects
 * - Supports both light and dark themes with appropriate contrast ratios
 * - Handles disabled state and provides smooth hover animations with scale transform
 * - Icons are dynamically assigned based on option keywords (note, task, meeting, email)
 * - On button click, adds the button text to chat input and submits automatically
 * - Direct integration with chat input system for seamless user interaction
 * - No event bus dependency - uses direct function calls for better performance
 */

import React from 'react';

interface UserChoiceProps {
  options: string[];
  onChoice: (choice: string) => void;
  disabled?: boolean;
  className?: string;
  onSubmitMessage?: (message: string) => void;
}

const UserChoice: React.FC<UserChoiceProps> = ({
  options,
  onChoice,
  disabled = false,
  className = '',
  onSubmitMessage
}) => {
  const handleChoice = (choice: string) => {
    // First call the original onChoice callback
    onChoice(choice);

    // If onSubmitMessage is provided, use it to add to chat input and submit
    if (onSubmitMessage) {
      onSubmitMessage(choice);
    }
  };
  const getIcon = (option: string) => {
    const lowercaseOption = option.toLowerCase();

    if (lowercaseOption.includes('note')) {
      return (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    }

    if (lowercaseOption.includes('task')) {
      return (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      );
    }

    if (lowercaseOption.includes('meeting')) {
      return (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }

    if (lowercaseOption.includes('email')) {
      return (
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    }

    // Default icon
    return (
      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  };

  return (
    <div className={`p-4 my-2 ${className}`}>
      <div className="grid grid-cols-1 gap-2">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleChoice(option)}
            disabled={disabled}
            className="flex px-1 py-1 text-sm font-light text-gray-500 hover:text-gray-800 dark:text-gray-500 dark:hover:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-gray-500 hover:shadow-md transform hover:scale-105 text-center disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:hover:bg-white dark:disabled:hover:bg-gray-800"
          >
            {getIcon(option)}
            {option}
          </button>
        ))}
      </div>
    </div>
  );
};

export default UserChoice;
